import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import PhotoPlaceholder from "../PhotoPlaceholder";
import { SizeGuide } from "./SizeGuide";
import { OptionPicker, pickVariant } from "./OptionPicker";
import { assetUrl, money } from "../../lib/shopApi";
import { cart } from "../../lib/cart";

const initialSel = (variants) => {
  const v = variants[0] || {};
  return { color: v.color || "", size: v.size || "", label: v.id };
};

export const ProductDialog = ({ product, onClose, onAdded }) => {
  const [sel, setSel] = useState({});
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (product) {
      setSel(initialSel(product.variants));
      setQty(1);
      setImgIdx(0);
    }
  }, [product]);

  if (!product) return null;
  const variant = pickVariant(product.variants, sel);
  const images = product.images?.length ? product.images : variant?.image ? [variant.image] : [];
  const mainImage = imgIdx === -1 ? variant?.image || images[0] : images[imgIdx] || variant?.image;

  const add = () => {
    if (!variant) return toast("Please choose an option first.");
    cart.add({
      product_id: product.id, variant_id: variant.id, title: product.title,
      variant_label: variant.label, price: variant.price, image: variant.image || images[0] || "", quantity: qty,
    });
    toast(`${product.title} added to your basket.`);
    onAdded();
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-[#F9F6F0] border-[#E7DFD5] p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="shop-product-dialog">
        <div className="grid md:grid-cols-2">
          <div className="p-6 bg-white">
            <div className="aspect-square rounded overflow-hidden bg-white">
              {mainImage ? (
                <img src={assetUrl(mainImage)} alt={product.title} className="w-full h-full object-contain" />
              ) : (
                <PhotoPlaceholder label={product.title} aspect="square" accent="#C8A876" />
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {images.map((src, i) => (
                  <button key={src} type="button" onClick={() => setImgIdx(i)} data-testid={`shop-dialog-thumb-${i}`}
                    className={`w-16 h-16 rounded border overflow-hidden ${i === imgIdx ? "border-[#4B3A2F]" : "border-[#E7DFD5]"}`}>
                    <img src={assetUrl(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-8 flex flex-col">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D62839] mb-2">{product.kind}</p>
            <DialogTitle className="font-display text-3xl text-[#4B3A2F] leading-tight mb-3">{product.title}</DialogTitle>
            <DialogDescription className="font-body text-[#2C2724]/85 leading-relaxed whitespace-pre-line mb-6">
              {product.description}
            </DialogDescription>
            <OptionPicker variants={product.variants} sel={sel} setSel={(s) => { setSel(s); setImgIdx(-1); }} />
            <SizeGuide guideKey={product.size_guide} />
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-[#D9CBB8] rounded-full bg-white">
                <button type="button" aria-label="Less" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2" data-testid="shop-dialog-qty-minus"><Minus size={14} /></button>
                <span className="w-8 text-center" data-testid="shop-dialog-qty">{qty}</span>
                <button type="button" aria-label="More" onClick={() => setQty(Math.min(25, qty + 1))} className="p-2" data-testid="shop-dialog-qty-plus"><Plus size={14} /></button>
              </div>
              <span className="font-display text-2xl text-[#4B3A2F]" data-testid="shop-dialog-price">
                {variant ? money(variant.price) : "—"}
              </span>
            </div>
            <button type="button" onClick={add} disabled={!variant} data-testid="shop-dialog-add-to-cart"
              className="mt-auto rounded-full bg-[#D62839] text-white px-8 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#b51f2e] disabled:opacity-50 transition-colors duration-300">
              {variant ? "Add to basket" : "This option isn't available"}
            </button>
            <p className="text-xs text-[#6D5A50] mt-3 text-center">Printed just for you &middot; ships to the US &amp; Canada</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
